"use client";

import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/utils/utils";
import { Button } from "./button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../container/popover";
import { Calendar } from "./calendar";
import { Text } from "../information/text";

/**
 * Utility: format Date to "YYYY-MM-DD" for form submission.
 */
function dateToISO(d: Date): string {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export type DatePickerProps = {
  /**
   * Controlled value. When provided, the component becomes controlled.
   */
  value?: Date | null;
  /**
   * Uncontrolled initial value.
   */
  defaultValue?: Date | null;
  /**
   * Change handler. Called for both controlled and uncontrolled modes.
   */
  onChange?: (value: Date | null) => void;

  placeholder?: string;

  // Form/field attributes
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;

  // Styling
  className?: string; // wrapper
  triggerClassName?: string; // button
  panelClassName?: string; // dropdown panel

  /**
   * Custom display formatter for the selected date.
   */
  format?: (date: Date) => string;

  /**
   * Extra props forwarded to the Calendar (excluding selection wiring).
   */
  calendarProps?: Omit<
    React.ComponentProps<typeof Calendar>,
    "mode" | "selected" | "onSelect" | "className"
  >;
} & Omit<
  React.ComponentProps<typeof Button>,
  "onChange" | "value" | "defaultValue" | "children" | "disabled"
>;

/**
 * DatePicker
 * - Dropdown calendar using the shared Calendar component (react-day-picker wrapper).
 * - Supports controlled (value) and uncontrolled (defaultValue) usage.
 * - Renders a hidden input (id/name/required) for form integration.
 */
const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  function DatePicker(
    {
      value,
      defaultValue = null,
      onChange,
      placeholder = "Pick a date",
      disabled,
      required,
      name,
      id,
      className,
      triggerClassName,
      panelClassName,
      format,
      calendarProps,
      ...buttonProps
    },
    ref
  ) {
    const isControlled = value !== undefined;
    const [internal, setInternal] = React.useState<Date | null>(defaultValue);
    const selected = (isControlled ? value : internal) ?? null;

    const [open, setOpen] = React.useState(false);

    const displayLabel = React.useMemo(() => {
      if (!selected) return undefined;
      if (format) return format(selected);
      try {
        return selected.toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "2-digit",
        });
      } catch {
        return dateToISO(selected);
      }
    }, [selected, format]);

    const emitChange = (d: Date | null) => {
      if (isControlled) {
        onChange?.(d);
      } else {
        setInternal(d);
        onChange?.(d);
      }
    };

    return (
      <div className={cn("relative inline-flex w-full", className)}>
        {/* Hidden input for forms and label association */}
        <input
          ref={ref}
          type="hidden"
          id={id}
          name={name}
          required={required}
          value={selected ? dateToISO(selected) : ""}
          readOnly
          aria-hidden="true"
        />

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div className="w-full border px-4 h-8 rounded-lg text-body flex space-x-1 items-center">
              <CalendarIcon className="mr-2 h-4 w-4" />
              <Text>{displayLabel ?? placeholder}</Text>
            </div>
          </PopoverTrigger>

          <PopoverContent
            align="start"
            className={cn("w-[300px] p-0", panelClassName)}
          >
            <Calendar
              mode="single"
              selected={selected ?? undefined}
              onSelect={(d?: Date) => {
                emitChange(d ?? null);
                setOpen(false);
              }}
              {...calendarProps}
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  }
);

export { DatePicker };
